import {
  Criterion,
  IaTargetAge,
  IaTargetResult,
  IaTargetValues,
  ParticipantField,
} from '@app/api';
import { getLocalesFromCountries } from '@app/form';
import {
  createEmptyCriterionFromParticipantField,
  setCriterionValues,
} from 'lib/criteria';
import { isFieldAllowed } from 'lib/criteriaUtils';

export interface IaTargetCriterion {
  field: ParticipantField;
  criterion: Criterion;
  reason?: string;
  selectedCount: number;
}

const baseCriterion = (
  field: ParticipantField,
  criteria: Criterion[],
): Criterion =>
  criteria.find((criterion) => criterion.fieldRef === field.name) ||
  createEmptyCriterionFromParticipantField(field);

const buildAge = (
  age: IaTargetAge,
  field: ParticipantField,
  criteria: Criterion[],
): Criterion | null => {
  const floor = field.validation?.min ?? null;
  const ceiling = field.validation?.max ?? null;

  const min =
    typeof age.min === 'number' && floor !== null
      ? Math.max(age.min, floor)
      : age.min ?? floor;
  const max =
    typeof age.max === 'number' && ceiling !== null
      ? Math.min(age.max, ceiling)
      : age.max ?? ceiling;

  if (typeof min === 'number' && typeof max === 'number' && min > max) {
    return null;
  }

  return setCriterionValues(
    baseCriterion(field, criteria),
    [{ min: min ?? null, max: max ?? null }],
    [{ ref: '', label: '', value: { min: floor, max: ceiling } }],
  );
};

const buildValues = (
  suggested: IaTargetValues,
  field: ParticipantField,
  criteria: Criterion[],
  normalize: (value: string) => string = (value) => value.toUpperCase(),
  accepted?: string[],
): Criterion | null => {
  const wanted = (suggested.values || []).map((value) =>
    normalize(String(value)),
  );
  const values = (
    field.choices?.length
      ? wanted.filter((value) =>
          field.choices?.some((choice) => String(choice.value) === value),
        )
      : wanted
  ).filter((value) => !accepted || accepted.includes(value));

  if (!values.length) {
    return null;
  }
  return setCriterionValues(baseCriterion(field, criteria), values);
};

/**
 * The countries the languages are restricted to: those the suggestion sets, or else
 * those the study already targets.
 */
const targetedCountries = (
  items: IaTargetCriterion[],
  criteria: Criterion[],
): string[] => {
  const countryCriterion =
    items.find(({ field }) => field.name === 'countryKey')?.criterion ||
    criteria.find(({ fieldRef }) => fieldRef === 'countryKey');
  return (countryCriterion?.allowList || []) as string[];
};

/**
 * On an offer restricted to the locales of the targeted countries, the languages
 * the designer could not pick by hand are dropped.
 */
const acceptedLocales = (
  field: ParticipantField,
  allowedFields: string[] | undefined,
  countries: string[],
): string[] | undefined => {
  const restricted =
    !!allowedFields &&
    allowedFields.includes(field.name + '_restricted') &&
    !allowedFields.includes(field.name);
  return restricted ? getLocalesFromCountries(countries) : undefined;
};

const CHOICE_CRITERIA: (keyof IaTargetResult & string)[] = [
  'familyStatus',
  'professionalSituation',
  'educationLevel',
];

/**
 * Turns the answer of the "target" prompt into the criteria of the study. A key
 * the model left out is a criterion the study does not state, so it is skipped
 * rather than reset. A criterion the offer does not allow is skipped too.
 */
export const buildTargetFromIaSuggestion = (
  result: IaTargetResult | null | undefined,
  fieldsByName: { [name: string]: ParticipantField },
  criteria: Criterion[],
  allowedFields?: string[],
): IaTargetCriterion[] => {
  if (!result) {
    return [];
  }

  const items: IaTargetCriterion[] = [];
  const add = (
    field: ParticipantField | undefined,
    criterion: Criterion | null,
    reason?: string,
  ) => {
    if (field && criterion && isFieldAllowed(allowedFields, field)) {
      items.push({
        field,
        criterion,
        reason,
        selectedCount: (criterion.allowList || []).length,
      });
    }
  };

  const ageField = fieldsByName.birthdate;
  if (result.age && ageField) {
    add(ageField, buildAge(result.age, ageField, criteria), result.age.reason);
  }

  const genderField = fieldsByName.gender;
  if (result.gender && genderField) {
    add(
      genderField,
      buildValues(result.gender, genderField, criteria),
      result.gender.reason,
    );
  }

  const countryField = fieldsByName.countryKey;
  if (result.country && countryField) {
    add(
      countryField,
      buildValues(result.country, countryField, criteria),
      result.country.reason,
    );
  }

  const localesField = fieldsByName.locales;
  if (result.language && localesField) {
    add(
      localesField,
      buildValues(
        result.language,
        localesField,
        criteria,
        (value) => value.toLowerCase(),
        acceptedLocales(
          localesField,
          allowedFields,
          targetedCountries(items, criteria),
        ),
      ),
      result.language.reason,
    );
  }

  CHOICE_CRITERIA.forEach((name) => {
    const suggested = result[name] as IaTargetValues | undefined;
    const field = fieldsByName[name];
    if (suggested && field) {
      add(field, buildValues(suggested, field, criteria), suggested.reason);
    }
  });

  return items;
};

/**
 * Replaces the criteria the suggestion covers and leaves the others untouched.
 */
export const applyIaTargetCriteria = (
  criteria: Criterion[],
  suggested: IaTargetCriterion[],
): Criterion[] => {
  const byFieldRef = suggested.reduce((acc, { criterion }) => {
    acc[criterion.fieldRef] = criterion;
    return acc;
  }, {} as { [fieldRef: string]: Criterion });

  const updated = criteria.map(
    (criterion) => byFieldRef[criterion.fieldRef] || criterion,
  );
  const added = suggested
    .filter(
      ({ criterion }) =>
        !criteria.some((current) => current.fieldRef === criterion.fieldRef),
    )
    .map(({ criterion }) => criterion);

  return [...updated, ...added];
};
